import { UniqueAdvertiser } from '@/common/models';
import { IUniqueAdvertiserAPI } from '@/common/models/advertisers/UniqueAdvertiser';
import rootState from '@/store/state';
import formatNumber from '@/utils/NumberFormatter';
import { IExtraValidation } from '@/validator/Validatable';
import i18n from '@/vueI18n';

export interface IAdvertiserAPI {
  advertiser_id: number;
  domain: string;
  iab_categories: string[];
  min_seconds_between_ad_plays: number;
  name: string;
  unique_advertiser?: IUniqueAdvertiserAPI;
}

export interface ITTDAdvertiserAPI {
  ttd_advertiser_currency: string;
  ttd_advertiser_id: string;
  ttd_advertiser_name: string;
}

export default class Advertiser {
  id?: number | string;

  uniqueAdvertiserID?: number;

  uniqueAdvertiserReplacementID?: number;

  name?: string;

  domain?: string;

  iabCategories?: string[];

  minSecondsBetweenAdPlays?: number;

  uniqueAdvertiser?: UniqueAdvertiser;

  description?: string;

  // TTD
  currency?: string;

  constructor(
    id: number | string = -1,
    uniqueAdvertiserID: number = null,
    uniqueAdvertiserReplacementID: number = null,
    name: string = null,
    domain: string = null,
    iabCategories: string[] = [],
    minSecondsBetweenAdPlays = 0,
    uniqueAdvertiser: UniqueAdvertiser = null,
    currency: string = null,
  ) {
    this.id = id;
    this.uniqueAdvertiserID = uniqueAdvertiserID;
    this.uniqueAdvertiserReplacementID = uniqueAdvertiserReplacementID;
    this.name = name;
    this.domain = domain;
    this.iabCategories = iabCategories;
    this.minSecondsBetweenAdPlays = minSecondsBetweenAdPlays;
    this.uniqueAdvertiser = uniqueAdvertiser;
    this.description = domain != null && domain.trim() !== '' ? `${name} (${domain})` : name;

    // TTD
    this.currency = currency;
  }

  get minSecondsBetweenAdPlaysFormatted(): string {
    return `${formatNumber(this.minSecondsBetweenAdPlays, '0,0')} ${i18n.global.t('names.second', { n: 2 })}`;
  }

  get idAndName(): string {
    return `${this.id} - ${this.name}`;
  }

  getNewAdvertiserExtraValidation(advertisers: Advertiser[]): Record<string, IExtraValidation> {
    const extras: Record<string, IExtraValidation> = {};

    extras.existingAdvertisers = {
      value: advertisers.map((a) => a.name),
      rules: null,
    };
    extras.newAdvertiserName = {
      value: this.name,
      rules: 'required|not_in_use:existingAdvertisers|max:100|not_empty_or_whitespace',
    };
    extras.newAdvertiserDomain = {
      value: this.domain,
      rules: 'required|domain|max:100|not_empty_or_whitespace',
    };

    if (
      this.domain != null &&
      (this.domain.indexOf('www') === 0 ||
        this.domain.indexOf('http://') === 0 ||
        this.domain.indexOf('https://') === 0)
    ) {
      extras.newAdvertiserDomain = {
        message: `${i18n.global.t('messages.advertiserDomainShouldNotStartWithHttpOrWww')}\n${i18n.global.t('messages.fieldDescriptionAdvertiserDomain')}`,
        preEvaluated: true,
      };
    }

    const isAdvertiserDomainValid =
      advertisers.find((x) => x.domain != null && x.domain.localeCompare(this.domain) === 0) === undefined;
    if (!isAdvertiserDomainValid) {
      extras.newAdvertiserDomain = {
        message: i18n.global.t('messages.newAdvertiserDomainAlreadyInUse'),
        preEvaluated: true,
      };
    }

    return extras;
  }

  get uniquerOrReplacementAdvertiser(): UniqueAdvertiser {
    if (this.uniqueAdvertiser == null) {
      return null;
    }
    return this.uniqueAdvertiser.replacementUniqueAdvertisers
      ? this.uniqueAdvertiser.replacementUniqueAdvertisers
      : this.uniqueAdvertiser;
  }

  get uniqueAdvertiserDomain(): string {
    if (this.uniquerOrReplacementAdvertiser?.advertiserDomains?.length > 0) {
      return this.uniquerOrReplacementAdvertiser.advertiserDomains[0].domain;
    }
    return '';
  }

  get uniqueAdvertiserName(): string {
    if (this.uniquerOrReplacementAdvertiser?.name != null) {
      return this.uniquerOrReplacementAdvertiser.name;
    }
    return '';
  }

  get uniqueAdvertiserLogoUrl(): string | null {
    if (this.uniquerOrReplacementAdvertiser?.logoUrl != null) {
      return this.uniquerOrReplacementAdvertiser.logoUrl;
    }
    return null;
  }

  static get isTTDOrganization(): boolean {
    return rootState?.orgApp?.organization?.isTTD;
  }

  static fromApi(apiObject: IAdvertiserAPI | ITTDAdvertiserAPI): Advertiser {
    if (Advertiser.isTTDOrganization) {
      const ttdApiObject = apiObject as ITTDAdvertiserAPI;
      const id = ttdApiObject.ttd_advertiser_id;
      const name = ttdApiObject.ttd_advertiser_name;
      const currency = ttdApiObject.ttd_advertiser_currency;
      return new Advertiser(id, null, null, `${name} (${id})`, null, [], 0, null, currency);
    }

    if ('advertiser_id' in apiObject) {
      return new Advertiser(
        apiObject.advertiser_id,
        apiObject.unique_advertiser != null ? apiObject.unique_advertiser.uniqueadvertiser_id : null,
        apiObject.unique_advertiser != null ? apiObject.unique_advertiser.replacement_id : null,
        apiObject.name,
        apiObject.domain,
        apiObject.iab_categories,
        apiObject.min_seconds_between_ad_plays,
        apiObject.unique_advertiser ? UniqueAdvertiser.fromApi(apiObject.unique_advertiser) : null,
      );
    }
    return null;
  }

  toApi(): IAdvertiserAPI {
    return {
      advertiser_id: (this.id as number) >= 0 ? (this.id as number) : undefined,
      name: this.name,
      domain: this.domain,
      iab_categories: this.iabCategories,
      min_seconds_between_ad_plays: this.minSecondsBetweenAdPlays,
    };
  }

  toApiWithUniqueAdvertiser(): IAdvertiserAPI {
    const advertiserWithoutUniqueAdvertiser = this.toApi();

    return {
      ...advertiserWithoutUniqueAdvertiser,
      ...(this.uniqueAdvertiser ? { unique_advertiser: this.uniqueAdvertiser.toApi() } : {}),
    };
  }
}
